import type { ReactNode } from 'react'

import { cn } from 'src/utils'

/**
 * The frame every widget on this dashboard shares: a bordered card with a header
 * row and a body. Extracted from `AgendaWidgetView` when the second widget
 * arrived, so the two cannot drift apart.
 *
 * Deliberately not in here:
 *
 * - **No `loading` prop.** Each widget's loading state is specific — the agenda
 *   shows one spinner in place of the list, the prospects strip skeletons each
 *   number individually so its labels stay put. A shared prop would have to
 *   render *instead of* `children` to avoid doubling up with a widget's own
 *   empty state, which rules out the per-number case entirely.
 * - **No empty state.** Same reason: the copy is per widget and depends on data
 *   the shell never sees.
 *
 * `title` and `controls` are opaque nodes rather than strings so a widget keeps
 * full control of its own typography and buttons.
 */
export interface WidgetShellProps {
  /** Left of the header. A node, not a string — the agenda passes two spans. */
  title: ReactNode
  /** Right of the header, usually icon buttons. Omit for a title-only header. */
  controls?: ReactNode
  children: ReactNode
  /**
   * `true` (default) fills the available height and scrolls the body — what a
   * full-column widget like the agenda needs. `false` sizes to content and does
   * not scroll, for a band that sits above other widgets.
   *
   * Named `fillHeight` rather than `fill` because `fill` is an SVG attribute and
   * every icon in this codebase carries `fill-none`.
   */
  fillHeight?: boolean
  className?: string
}

const WidgetShell = ({
  title,
  controls,
  children,
  fillHeight = true,
  className,
}: WidgetShellProps) => (
  <div
    className={cn(
      'flex min-w-0 flex-col rounded border border-doubleNickel-gray-200 p-3 shadow-sm',
      fillHeight ? 'h-full flex-1 overflow-hidden' : 'flex-shrink-0',
      className
    )}
  >
    <div className="mb-2 flex flex-row items-center justify-between gap-2">
      {title}
      {controls}
    </div>

    <div
      data-testid="widget-shell-body"
      className={cn(
        'flex min-w-0 flex-col',
        fillHeight && 'min-h-0 flex-1 overflow-auto'
      )}
    >
      {children}
    </div>
  </div>
)

export default WidgetShell
